import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Tab,
  Tabs,
  Box,
  Typography,
  Grid,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Card
} from '@mui/material';
import { useDispatch } from 'react-redux';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TableChartIcon from '@mui/icons-material/TableChart';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CreditScoreIcon from '@mui/icons-material/CreditScore';

import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';
import {
  getDataSourceValidate,
  getDataSourceView
} from '../../store/Slice/DataSource/DataSourceSlice';
import { fDateNew } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import { pillTabsSx } from '../Global/Styling';

const STATIC_TABS = [
  { id: 1, label: 'View Dataset', icon: <TableChartIcon /> },
  { id: 2, label: 'Valid Data Report', icon: <FactCheckIcon /> },
  { id: 3, label: 'Check Bureau Report', icon: <CreditScoreIcon /> }
];

const getFieldIcon = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('name') && !l.includes('dataset') && !l.includes('file')) {
    return <PersonOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('dataset') || l.includes('import method')) {
    return <EventNoteOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('file name') || l.includes('column structure')) {
    return <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('size') || l.includes('storage')) {
    return <StorageOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('records') || l.includes('rows') || l.includes('columns')) {
    return <TableChartOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('uploaded') || l.includes('modified') || l.includes('date')) {
    return <CalendarTodayOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('imported') || l.includes('valid')) {
    return <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('rejected') || l.includes('invalid')) {
    return <HighlightOffOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('duplicate')) {
    return <ContentCopyOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />;
};

const getIconBoxStyle = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('imported') || l.includes('valid') || l.includes('pass')) {
    return { bgcolor: '#f0fdf4', color: '#10b981' };
  }
  if (l.includes('rejected') || l.includes('invalid')) {
    return { bgcolor: '#fef2f2', color: '#ef4444' };
  }
  if (l.includes('duplicate') || l.includes('no match') || l.includes('pending')) {
    return { bgcolor: '#fffbeb', color: '#f59e0b' };
  }
  if (l.includes('total') || l.includes('records') || l.includes('columns') || l.includes('rows')) {
    return { bgcolor: '#f5f3ff', color: '#6366f1' };
  }
  if (l.includes('dataset') || l.includes('file') || l.includes('size')) {
    return { bgcolor: '#ecfeff', color: '#0891b2' };
  }
  if (l.includes('uploaded') || l.includes('modified')) {
    return { bgcolor: '#eff6ff', color: '#3b82f6' };
  }
  return { bgcolor: '#f0f9ff', color: '#0284c7' };
};

const getStatusBadgeStyle = (status) => {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE' || s === 'COMPLETED' || s === 'SUBMITTED' || s === 'PASSED') {
    return { bgcolor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' };
  }
  if (s === 'IN PROGRESS' || s === 'PENDING' || s === 'DUPLICATE' || s === 'DND') {
    return { bgcolor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' };
  }
  if (s === 'INVALID' || s === 'NEGATIVE' || s === 'REJECTED') {
    return { bgcolor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };
  }
  return { bgcolor: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' };
};

export default function ViewDataSet() {
  const [fileTypeId, setFileTypeId] = useState(1);

  const { DataSourceViewData, DataSourceValidateData } = useDataSourceSelector();
  const location = useLocation();
  const navigate = useNavigate();
  const { rowId } = location.state || {};
  const dispatch = useDispatch();

  const handleChangeTab = useCallback((_, newValue) => {
    setFileTypeId(newValue);
  }, []);

  const getViewDataSourceListApi = useCallback(() => {
    dispatch(getDataSourceView({ rowId }));
  }, [dispatch, rowId]);

  useEffect(() => {
    getViewDataSourceListApi();
  }, [getViewDataSourceListApi]);

  const getDataSourceValidateApi = useCallback(() => {
    dispatch(getDataSourceValidate({ rowId }));
  }, [dispatch, rowId]);

  useEffect(() => {
    getDataSourceValidateApi();
  }, [getDataSourceValidateApi]);

  const validRecords = useMemo(
    () => Number(DataSourceValidateData?.validRecords || 0),
    [DataSourceValidateData]
  );
  const totalRecords = useMemo(
    () => Number(DataSourceValidateData?.totalRecords || 0),
    [DataSourceValidateData]
  );

  const datasetInfoFields = useMemo(
    () => [
      {
        label: 'Dataset Name',
        value: DataSourceViewData?.datasetInfo?.datasetName || NOT_AVAILABLE
      },
      {
        label: 'Import Method',
        value: DataSourceViewData?.datasetInfo?.importMethod || NOT_AVAILABLE
      },
      {
        label: 'Total Records',
        value: DataSourceViewData?.datasetInfo?.totalRecords || NOT_AVAILABLE
      },
      {
        label: 'Total Columns',
        value: DataSourceViewData?.datasetInfo?.totalColumns || NOT_AVAILABLE
      },
      {
        label: 'Uploaded On',
        value: fDateNew(DataSourceViewData?.datasetInfo?.uploadedOn) || NOT_AVAILABLE
      },
      {
        label: 'Modified On',
        value: fDateNew(DataSourceViewData?.datasetInfo?.modifiedOn) || NOT_AVAILABLE
      }
    ],
    [DataSourceViewData]
  );

  const sourceFileFields = useMemo(
    () => [
      {
        label: 'File Name',
        value: DataSourceViewData?.fileDetails?.fileName || NOT_AVAILABLE
      },
      {
        label: 'File Size',
        value: DataSourceViewData?.fileDetails?.fileSizeMb ?? NOT_AVAILABLE
      },
      {
        label: 'Total Rows in File',
        value: DataSourceViewData?.fileDetails?.totalRowsInFile ?? NOT_AVAILABLE
      }
    ],
    [DataSourceViewData]
  );

  const importSummaryFields = useMemo(
    () => [
      {
        label: 'Imported Records',
        value: DataSourceViewData?.importSummary?.importedRecords ?? NOT_AVAILABLE
      },
      {
        label: 'Rejected Rows',
        value: DataSourceViewData?.importSummary?.rejectedRows ?? NOT_AVAILABLE
      },
      {
        label: 'Duplicate Records',
        value: DataSourceViewData?.importSummary?.duplicateRecords ?? NOT_AVAILABLE
      }
    ],
    [DataSourceViewData]
  );

  const currentStatus = DataSourceViewData?.datasetInfo?.status || NOT_AVAILABLE;
  const statusBadgeStyle = getStatusBadgeStyle(currentStatus);

  return (
    <Page title="Partner Management System || Data Source View">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {/* Top Header Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 3.5, gap: 2 }}
          >
            {/* Left: Back Button & Title */}
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/data')}
                sx={{
                  color: '#0284c7',
                  borderColor: '#bae6fd',
                  bgcolor: '#ffffff',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '13px',
                  textTransform: 'none',
                  py: 0.9,
                  px: 2,
                  boxShadow: '0 2px 6px rgba(2, 132, 199, 0.06)',
                  '&:hover': {
                    bgcolor: '#f0f9ff',
                    borderColor: '#0284c7'
                  }
                }}
              >
                Back to Dataset List
              </Button>

              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: '#0f172a',
                    fontSize: { xs: '20px', sm: '24px' },
                    letterSpacing: '-0.3px',
                    lineHeight: 1.2
                  }}
                >
                  Dataset Details & Report
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.25 }}>
                  View complete dataset details, import summary, column mappings, and verification
                  reports.
                </Typography>
              </Box>
            </Stack>

            {/* Right: Breadcrumbs & Current Status */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <BrudCrumbs />
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                  Current Status:
                </Typography>
                <Box
                  sx={{
                    bgcolor: statusBadgeStyle.bgcolor,
                    color: statusBadgeStyle.color,
                    px: 1.75,
                    py: 0.4,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    border: statusBadgeStyle.border
                  }}
                >
                  {currentStatus}
                </Box>
              </Stack>
            </Stack>
          </Stack>

          {/* Modern Tabs Bar */}
          <Box sx={{ mb: 3.5 }}>
            <Tabs value={fileTypeId} onChange={handleChangeTab} sx={pillTabsSx}>
              {STATIC_TABS.map((tab) => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  disableRipple
                />
              ))}
            </Tabs>
          </Box>

          {/* Dynamic Content Per Tab */}
          <Scrollbar>
            {/* -------- TAB PANEL 1 : VIEW DATASET -------- */}
            <TabPanel value={fileTypeId} index={1}>
              <Stack spacing={3}>
                {/* Dataset Information Card */}
                <Card
                  sx={{
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    p: { xs: 2.5, md: 3 },
                    bgcolor: '#ffffff'
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          bgcolor: '#0284c7',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <StorageOutlinedIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                        Dataset Information
                      </Typography>
                    </Stack>
                  </Stack>

                  <Grid container spacing={3}>
                    {datasetInfoFields.map((field, idx) => {
                      const iconStyle = getIconBoxStyle(field.label);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Stack direction="row" spacing={1.75} alignItems="center">
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                bgcolor: iconStyle.bgcolor,
                                color: iconStyle.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              {getFieldIcon(field.label)}
                            </Box>
                            <Box>
                              <Typography
                                sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}
                              >
                                {field.label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: 700,
                                  color: '#0f172a',
                                  mt: 0.2
                                }}
                              >
                                {field.value}
                              </Typography>
                            </Box>
                          </Stack>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {DataSourceViewData?.datasetInfo?.datasetDescription && (
                    <Box
                      sx={{
                        mt: 3,
                        pt: 2,
                        borderTop: '1px dashed #e2e8f0'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          mb: 0.5
                        }}
                      >
                        Description
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#334155' }}>
                        {DataSourceViewData.datasetInfo.datasetDescription}
                      </Typography>
                    </Box>
                  )}
                </Card>

                {/* Source File & Import Summary Cards Grid */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                        p: { xs: 2.5, md: 3 },
                        bgcolor: '#ffffff'
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '8px',
                              bgcolor: '#0284c7',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                            Source File
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack spacing={2.5}>
                        {sourceFileFields.map((field, idx) => {
                          const iconStyle = getIconBoxStyle(field.label);
                          return (
                            <Stack direction="row" spacing={1.75} alignItems="center" key={idx}>
                              <Box
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: '12px',
                                  bgcolor: iconStyle.bgcolor,
                                  color: iconStyle.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                {getFieldIcon(field.label)}
                              </Box>
                              <Box>
                                <Typography
                                  sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}
                                >
                                  {field.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    mt: 0.2
                                  }}
                                >
                                  {field.value}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                        p: { xs: 2.5, md: 3 },
                        bgcolor: '#ffffff'
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '8px',
                              bgcolor: '#0284c7',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <FactCheckOutlinedIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                            Import Summary
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack spacing={2.5}>
                        {importSummaryFields.map((field, idx) => {
                          const iconStyle = getIconBoxStyle(field.label);
                          return (
                            <Stack direction="row" spacing={1.75} alignItems="center" key={idx}>
                              <Box
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: '12px',
                                  bgcolor: iconStyle.bgcolor,
                                  color: iconStyle.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                {getFieldIcon(field.label)}
                              </Box>
                              <Box>
                                <Typography
                                  sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}
                                >
                                  {field.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    mt: 0.2
                                  }}
                                >
                                  {field.value}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>

                {/* Column Structure Card */}
                <Card
                  sx={{
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    p: { xs: 2.5, md: 3 },
                    bgcolor: '#ffffff'
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          bgcolor: '#0284c7',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ViewColumnOutlinedIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                        Column Structure
                      </Typography>
                    </Stack>
                  </Stack>

                  <TableContainer
                    sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Column Name
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Data Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Sample Data
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {DataSourceViewData?.columnStructure
                          ?.filter(
                            (item) =>
                              item?.columnName !== 'company_id' &&
                              item?.columnName !== 'created_by' &&
                              item?.columnName !== 'loan_type_id'
                          )
                          ?.map((row) => (
                            <TableRow
                              key={row.columnName}
                              sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                            >
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: '#0284c7',
                                  fontSize: '13px',
                                  py: 1.5,
                                  borderBottom: '1px solid #f1f5f9'
                                }}
                              >
                                {row.columnName}
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  borderBottom: '1px solid #f1f5f9'
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    bgcolor: '#eff6ff',
                                    color: '#2563eb',
                                    px: 1.5,
                                    py: 0.3,
                                    borderRadius: '6px',
                                    fontSize: '11.5px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                    display: 'inline-block'
                                  }}
                                >
                                  {row.dataType}
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: '#64748b',
                                  fontSize: '13px',
                                  py: 1.5,
                                  borderBottom: '1px solid #f1f5f9'
                                }}
                              >
                                {row.sampleValue || NOT_AVAILABLE}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>

                {/* Record List Card */}
                <Card
                  sx={{
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    p: { xs: 2.5, md: 3 },
                    bgcolor: '#ffffff'
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          bgcolor: '#0284c7',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <TableChartOutlinedIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                        Record List
                      </Typography>
                    </Stack>
                  </Stack>

                  <TableContainer
                    sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            ID
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Name
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Phone
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Email
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Loan Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Amount
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            City
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#64748b',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              py: 1.5,
                              borderBottom: '1px solid #e2e8f0'
                            }}
                          >
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {DataSourceViewData?.sampleRecords &&
                        DataSourceViewData.sampleRecords.length > 0 ? (
                          DataSourceViewData.sampleRecords.map((row) => {
                            const rowBadge = getStatusBadgeStyle(row?.recordStatus);
                            return (
                              <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    color: '#0284c7',
                                    fontSize: '13px',
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                >
                                  {row.id}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    fontSize: '13px',
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                >
                                  {row?.data?.name || NOT_AVAILABLE}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#64748b',
                                    fontSize: '13px',
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                >
                                  {row?.data?.phone || NOT_AVAILABLE}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#64748b',
                                    fontSize: '13px',
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                >
                                  {row?.data?.email || NOT_AVAILABLE}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#334155',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                >
                                  {row?.data?.loan_type || NOT_AVAILABLE}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#0f172a',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                >
                                  {row?.data?.loan_amount || NOT_AVAILABLE}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#64748b',
                                    fontSize: '13px',
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                >
                                  {row?.data?.city || NOT_AVAILABLE}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      bgcolor: rowBadge.bgcolor,
                                      color: rowBadge.color,
                                      border: rowBadge.border,
                                      px: 1.5,
                                      py: 0.3,
                                      borderRadius: '20px',
                                      fontSize: '11.5px',
                                      fontWeight: 700,
                                      display: 'inline-block'
                                    }}
                                  >
                                    {row?.recordStatus || NOT_AVAILABLE}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              align="center"
                              sx={{ py: 3, color: '#94a3b8', fontSize: '13px' }}
                            >
                              No records found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Stack>
            </TabPanel>

            {/* -------- TAB PANEL 2 : VALID DATA REPORT -------- */}
            <TabPanel value={fileTypeId} index={2}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  p: { xs: 2.5, md: 3 },
                  bgcolor: '#ffffff'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FactCheckOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                      Data Validation Report
                    </Typography>
                  </Stack>
                </Stack>

                <Grid container spacing={3}>
                  {/* Status Banner */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #bbf7d0',
                        bgcolor: '#f0fdf4',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '12px',
                          bgcolor: '#dcfce7',
                          color: '#16a34a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 26 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#15803d', fontWeight: 800, fontSize: '15px' }}>
                          Validation Status: PASSED
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: '#166534', mt: 0.25, fontSize: '13px' }}
                        >
                          All records have been successfully validated against the required
                          criteria.
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Stat Cards */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        bgcolor: '#ffffff',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        Total Records
                      </Typography>
                      <Typography
                        sx={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', mt: 0.75 }}
                      >
                        {DataSourceValidateData?.totalRecords ?? NOT_AVAILABLE}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #bbf7d0',
                        bgcolor: '#f0fdf4'
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#15803d',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        Valid Records
                      </Typography>
                      <Typography
                        sx={{ fontSize: '26px', fontWeight: 800, color: '#16a34a', mt: 0.75 }}
                      >
                        {DataSourceValidateData?.validRecords ?? NOT_AVAILABLE}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #fecaca',
                        bgcolor: '#fef2f2'
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#dc2626',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        Invalid Records
                      </Typography>
                      <Typography
                        sx={{ fontSize: '26px', fontWeight: 800, color: '#dc2626', mt: 0.75 }}
                      >
                        {DataSourceValidateData?.invalidRecords ?? NOT_AVAILABLE}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #bbf7d0',
                        bgcolor: '#f0fdf4'
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#15803d',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        Pass Rate
                      </Typography>
                      <Typography
                        sx={{ fontSize: '26px', fontWeight: 800, color: '#16a34a', mt: 0.75 }}
                      >
                        {totalRecords > 0 ? ((validRecords / totalRecords) * 100).toFixed(0) : '0'}%
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Rules Checked Box */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        bgcolor: '#f8fafc'
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 800, color: '#0f172a', mb: 2, fontSize: '14.5px' }}
                      >
                        Validation Rules Checked:
                      </Typography>
                      <Stack spacing={1.5}>
                        {[
                          'Required fields are present',
                          'Email format validation passed',
                          'Phone number format is valid',
                          'Loan amount is within valid range',
                          'No duplicate records found'
                        ].map((rule, idx) => (
                          <Stack direction="row" spacing={1.5} alignItems="center" key={idx}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: '#dcfce7',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 800
                              }}
                            >
                              ✓
                            </Box>
                            <Typography
                              sx={{ color: '#334155', fontSize: '13.5px', fontWeight: 500 }}
                            >
                              {rule}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Card>
            </TabPanel>

            {/* -------- TAB PANEL 3 : BUREAU CHECK REPORT -------- */}
            <TabPanel value={fileTypeId} index={3}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  p: { xs: 2.5, md: 3 },
                  bgcolor: '#ffffff'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <HourglassEmptyOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                      Bureau Check Report
                    </Typography>
                  </Stack>
                </Stack>

                <Grid container spacing={3}>
                  {/* Status Banner */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #fde68a',
                        bgcolor: '#fffbeb',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '12px',
                          bgcolor: '#fef3c7',
                          color: '#b45309',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <HourglassEmptyOutlinedIcon sx={{ fontSize: 26 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#b45309', fontWeight: 800, fontSize: '15px' }}>
                          Bureau Check Status: IN PROGRESS
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: '#92400e', mt: 0.25, fontSize: '13px' }}
                        >
                          Bureau verification is being processed. This may take 24-48 hours to
                          complete.
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Stat Cards */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        bgcolor: '#ffffff',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        Records Submitted
                      </Typography>
                      <Typography
                        sx={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', mt: 0.75 }}
                      >
                        1,250
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #bbf7d0',
                        bgcolor: '#f0fdf4'
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#15803d',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        Bureau Match Found
                      </Typography>
                      <Typography
                        sx={{ fontSize: '26px', fontWeight: 800, color: '#16a34a', mt: 0.75 }}
                      >
                        892
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #fde68a',
                        bgcolor: '#fffbeb'
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#b45309',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        No Match
                      </Typography>
                      <Typography
                        sx={{ fontSize: '26px', fontWeight: 800, color: '#d97706', mt: 0.75 }}
                      >
                        358
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '14px',
                        border: '1px solid #bbf7d0',
                        bgcolor: '#f0fdf4'
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#15803d',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        Match Rate
                      </Typography>
                      <Typography
                        sx={{ fontSize: '26px', fontWeight: 800, color: '#16a34a', mt: 0.75 }}
                      >
                        71.4%
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Bureau Check Details Box */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        bgcolor: '#f8fafc'
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 800, color: '#0f172a', mb: 2, fontSize: '14.5px' }}
                      >
                        Bureau Check Details:
                      </Typography>
                      <Stack spacing={1.5}>
                        {[
                          'Credit Bureau: CIBIL (Credit Information Bureau India Limited)',
                          'Query Submission Date: 24 Dec 2024',
                          'Expected Completion: 26 Dec 2024',
                          'Average Credit Score (Matched): 680'
                        ].map((detail, idx) => (
                          <Stack direction="row" spacing={1.5} alignItems="center" key={idx}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: '#dbeafe',
                                color: '#2563eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 800
                              }}
                            >
                              ✓
                            </Box>
                            <Typography
                              sx={{ color: '#334155', fontSize: '13.5px', fontWeight: 500 }}
                            >
                              {detail}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Card>
            </TabPanel>
          </Scrollbar>
        </Container>
      </Box>
    </Page>
  );
}

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};
